import { TicketIcon, XCircleIcon, XIcon } from '@phosphor-icons/react';
import { Skeleton } from 'antd';
import { useState } from 'react';

import { Chip, type ChipIntent } from '@/components/common/chip';
import { Timeline } from '@/components/common/timeline';
import { UserAvatar } from '@/components/common/user-avatar';
import { Drawer } from '@/components/feedback/drawer';
import { customerAvatar } from '@/lib/avatar';

import {
  ACTIVITY_CHIP,
  CLAIM_STATUS_CHIP,
  formatWarrantyClaimDate,
  formatWarrantyDateTime,
  WARRANTY_REASONS,
  WARRANTY_STATUS_CHIP,
} from '../format';
import { useWarrantyClaim } from '../hooks';
import type { WarrantyClaimDetail, WarrantyClaimRow } from '../types';

import styles from './warranty-detail-drawer.module.scss';

export type WarrantyDetailDrawerProps = {
  open: boolean;
  id: string;
  /** The row the drawer was opened from. The real detail response is item-centric — it carries no
   *  `customerName`, `phone`, `city` or `orderDate` — so that context comes from the row the queue
   *  already fetched (the pattern 114698 applied to the Replacements sibling). */
  row: WarrantyClaimRow | null;
  onClose: () => void;
  /** Opens the reject popup for the claim on screen (ticket 04). */
  onReject?: (detail: WarrantyClaimDetail) => void;
  /** Opens the approve confirmation for the claim on screen (ticket 05). */
  onApprove?: (detail: WarrantyClaimDetail) => void;
};

/** The four-status timeline colourway, widened to accept any wire token: `timeline[].status` parses
 *  as a bare string (BE types it that way), so an unrecognised one degrades to a neutral chip instead
 *  of crashing on `undefined.intent`. Same call the order-scoped sibling made on 112445. */
function activityChip(status: string): { label: string; intent: ChipIntent } {
  const chips: Record<string, { label: string; intent: ChipIntent }> = ACTIVITY_CHIP;
  return chips[status] ?? { label: status, intent: 'neutral' };
}

function HeaderTitle({
  orderNumber,
  status,
}: {
  orderNumber?: string;
  status?: WarrantyClaimDetail['status'];
}) {
  const chip = status ? CLAIM_STATUS_CHIP[status] : undefined;
  // Every claim shows its Order ID, whatever the decision. Story 110178's original AC suppressed it
  // once Approved; the BA reversed that on 2026-08-05 ("Approved row will have view button
  // available … Order ID is not hidden"), which also settles the contradiction flagged in grilling
  // Q2 — the Figma for an Approved claim always drew the Order ID.
  return (
    <span className={styles.header}>
      <span className={styles.iconBadge}>
        <TicketIcon size={20} weight="fill" aria-hidden />
      </span>
      <span className={styles.headerText}>
        <span className={styles.headerHeading}>Warranty Claim Request</span>
        <span className={styles.headerSubtitleRow}>
          {orderNumber ? <span className={styles.headerSubtitle}>{orderNumber}</span> : null}
          {chip ? <Chip intent={chip.intent}>{chip.label}</Chip> : null}
        </span>
      </span>
    </span>
  );
}

function DetailBody({
  row,
  detail,
  isPending,
  onPreview,
}: {
  row: WarrantyClaimRow;
  detail?: WarrantyClaimDetail;
  isPending: boolean;
  onPreview: (url: string) => void;
}) {
  if (isPending || !detail) return <Skeleton active paragraph={{ rows: 6 }} />;

  const warranty = WARRANTY_STATUS_CHIP[detail.warrantyStatus];

  return (
    <>
      <div className={styles.infoCard}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Customer Name</span>
          <span className={styles.infoValue}>
            <UserAvatar
              name={row.customerName}
              src={customerAvatar(row.customerName)}
              label={row.customerName}
              size={18}
            />
          </span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Phone Number</span>
          <span className={styles.infoValue}>{row.phone}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Warranty Claim Date</span>
          <span className={styles.infoValueSecondary}>
            {formatWarrantyClaimDate(row.claimDate)}
          </span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Order Date &amp; Time</span>
          <span className={styles.infoValueSecondary}>{formatWarrantyDateTime(row.orderDate)}</span>
        </div>
        {/* Figma draws the customer's Address here, but **no endpoint in this contract carries one**
            — not the row, not the detail (asked of BE on 110178). City is the nearest thing either
            response holds, and it is what the Replacements sibling shows, so it takes the slot
            rather than leaving a permanently blank field. */}
        <div className={`${styles.infoItem} ${styles.infoItemFull}`}>
          <span className={styles.infoLabel}>City</span>
          <span className={styles.infoValue}>{row.city}</span>
        </div>
      </div>

      <div className={styles.reasonCard}>
        <h3 className={styles.reasonHeading}>{WARRANTY_REASONS[detail.reason]}</h3>
        {/* Reason and Description are both mandatory at submission (AC2), but the wire types the
            description nullable, so a claim from before that rule renders the reason alone. */}
        {detail.description ? <p className={styles.description}>{detail.description}</p> : null}
      </div>

      <div className={styles.deviceRow}>
        <span className={styles.deviceInfo}>
          <span className={styles.deviceName}>{detail.itemName}</span>
          <span className={styles.deviceSubtitle}>{detail.category ?? '-'}</span>
        </span>
        <Chip intent={warranty.intent}>{warranty.label}</Chip>
      </div>

      {/* AC3: no attachments → the section is omitted entirely, not an empty placeholder. A tile
          whose storage key did not resolve (`url: null`) is skipped rather than rendered broken. */}
      {detail.images.length ? (
        <ul className={styles.thumbGrid}>
          {detail.images.map((image) =>
            image.url ? (
              <li key={image.id}>
                <button
                  type="button"
                  className={styles.thumbBtn}
                  aria-label={`View attachment ${image.originalFileName ?? image.id}`}
                  onClick={() => onPreview(image.url!)}
                >
                  <img className={styles.thumbImg} src={image.url} alt="" />
                </button>
              </li>
            ) : null,
          )}
        </ul>
      ) : null}

      {detail.timeline.length ? (
        <>
          <h3 className={styles.timelineHeading}>Activity Timeline</h3>
          <Timeline
            items={detail.timeline.map((event) => {
              const chip = activityChip(event.status);
              return {
                // Uniform gray dot (Figma) — 'pending' maps the shared dot to --border-default.
                status: 'pending',
                title: <Chip intent={chip.intent}>{chip.label}</Chip>,
                timestamp: formatWarrantyDateTime(event.timestamp),
              };
            })}
          />
        </>
      ) : null}

      {/* Rejected only, and positioned below the timeline (AC5). */}
      {detail.status === 'rejected' && detail.rejectionReason ? (
        <div className={styles.rejection} role="alert">
          <span className={styles.rejectionHeading}>
            <XCircleIcon size={16} aria-hidden />
            Rejection Reason
          </span>
          <p className={styles.rejectionValue}>{detail.rejectionReason}</p>
        </div>
      ) : null}
    </>
  );
}

/** Read-only warranty-claim detail (ticket 03; reconciled to the real contract on 115394). The
 *  detail response is item-centric, so this merges it with the `row` the queue already fetched for
 *  customer/order/date context. Rendering is status-scoped: a Rejected claim adds its Rejection
 *  Reason below the timeline; a Pending claim gets Reject + Approve footer buttons (tickets 04/05),
 *  and a decided claim gets no footer. `useWarrantyClaim` is id-gated, so a closed drawer fetches
 *  nothing. */
export function WarrantyDetailDrawer({
  open,
  id,
  row,
  onClose,
  onReject,
  onApprove,
}: WarrantyDetailDrawerProps) {
  const { data: detail, isPending } = useWarrantyClaim(open ? id : '');
  const [preview, setPreview] = useState<string | null>(null);
  // Pending is the only actionable claim state (AC1) — Approved/Rejected are final, so they get no
  // footer at all.
  const actionable = detail?.status === 'pending' ? detail : undefined;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={549}
        hideFooter={!actionable}
        cancelText={actionable ? 'Reject' : undefined}
        onCancel={actionable ? () => onReject?.(actionable) : undefined}
        primaryText={actionable ? 'Approve' : undefined}
        onPrimary={actionable ? () => onApprove?.(actionable) : undefined}
        title={
          <HeaderTitle
            // The detail carries `orderNumber` too, but the row has it before the fetch settles, so
            // the header never flashes an empty subtitle.
            orderNumber={open ? (row?.orderNumber ?? detail?.orderNumber) : undefined}
            status={open ? detail?.status : undefined}
          />
        }
      >
        {open && row ? (
          <DetailBody row={row} detail={detail} isPending={isPending} onPreview={setPreview} />
        ) : null}
      </Drawer>

      {preview ? (
        <div className={styles.lightboxMask}>
          <button
            type="button"
            className={styles.lightboxBackdrop}
            aria-label="Close attachment preview"
            onClick={() => setPreview(null)}
          />
          <div
            className={styles.lightboxCard}
            role="dialog"
            aria-modal="true"
            aria-label="Attachment preview"
          >
            <button
              type="button"
              className={styles.lightboxClose}
              aria-label="Close attachment preview"
              onClick={() => setPreview(null)}
            >
              <XIcon size={14} aria-hidden />
            </button>
            <img className={styles.lightboxImg} src={preview} alt="" />
          </div>
        </div>
      ) : null}
    </>
  );
}
